// Base clientes: lista com busca e filtros + ficha do cliente em abas
// (resumo, atendimentos, técnicos, formulários, chips e zoneamento).
// (os técnicos ficam na tela de Contatos do Whaticket, seção "Técnico")
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Accordion, AccordionDetails, AccordionSummary, Avatar, Button, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Drawer, Grid, IconButton, InputAdornment, List, ListItem,
  ListItemAvatar, ListItemText, MenuItem, Paper, Tab, Table, TableBody, TableCell, TableHead, TableRow,
  Tabs, TextField, Tooltip, Typography, makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import GetAppIcon from "@material-ui/icons/GetApp";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";
import SearchIcon from "@material-ui/icons/Search";
import SimCardOutlinedIcon from "@material-ui/icons/SimCardOutlined";
import StoreOutlinedIcon from "@material-ui/icons/StoreOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import suporteApi, { baixar, mensagemErro } from "../api";
import { DialogCard, SITUACAO, copiar, dataBR, haQuanto } from "../comum";
import { FormCadastro, SITUACOES } from "../CadastroCliente";
import { RespostasEnvio, STATUS_FORM } from "../Formularios";

const POR_PAGINA = 50;
const COR_SITUACAO = { ATIVO: "#2e7d32", CANCELADO: "#c62828" };
const LARGURA_FICHA = 980;

const useStyles = makeStyles((theme) => ({
  filtros: {
    padding: "10px 14px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
    marginBottom: 8,
  },
  busca: { flex: "1 1 320px" },
  lista: { flex: 1, overflowY: "auto", ...theme.scrollbarStyles },
  item: { borderBottom: `1px solid ${theme.palette.divider}` },
  linha1: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  conta: { fontWeight: 700, fontFamily: "monospace", fontSize: 15 },
  lado: { textAlign: "right", minWidth: 210, marginLeft: 12, [theme.breakpoints.down("xs")]: { display: "none" } },
  fraco: { color: theme.palette.text.secondary, fontSize: 12.5 },
  ficha: { width: LARGURA_FICHA, maxWidth: "100vw", display: "flex", flexDirection: "column", height: "100%" },
  fichaTopo: { padding: "10px 16px 0", borderBottom: `1px solid ${theme.palette.divider}` },
  fichaCorpo: { flex: 1, overflowY: "auto", padding: 16, ...theme.scrollbarStyles },
  bloco: { padding: 14, height: "100%" },
  rotulo: { color: theme.palette.text.secondary, fontSize: 12, minWidth: 120, flex: "none" },
  campo: { display: "flex", gap: 8, padding: "3px 0", fontSize: 13.5, wordBreak: "break-word" },
  destaque: { borderLeft: `4px solid ${theme.palette.primary.main}` },
  tabela: { "& td, & th": { fontSize: 12.5, padding: "6px 8px" }, "& tbody tr": { cursor: "pointer" } },
  mono: { fontFamily: "monospace" },
}));

const periodo = (ini, fim) => {
  if (!ini) return "";
  const a = new Date(`${ini}T00:00:00`);
  const b = fim ? new Date(`${fim}T00:00:00`) : new Date();
  let anos = b.getFullYear() - a.getFullYear();
  let meses = b.getMonth() - a.getMonth();
  let dias = b.getDate() - a.getDate();
  if (dias < 0) { meses -= 1; dias += new Date(b.getFullYear(), b.getMonth(), 0).getDate(); }
  if (meses < 0) { anos -= 1; meses += 12; }
  return anos < 0 ? "" : `${anos} anos, ${meses} meses e ${dias} dias`;
};

const tecnicoTexto = (qra, nome) => [qra || qra === 0 ? `QRA ${qra}` : null, nome].filter(Boolean).join(" · ");

const ChipSituacao = ({ status }) => (
  <Chip size="small" label={status}
    style={{ background: COR_SITUACAO[status] || "#ef6c00", color: "#fff", height: 20, fontSize: 11 }} />
);

// ------------------------------------------------------------------ peças da ficha
const Campo = ({ rotulo, children }) => {
  const c = useStyles();
  if (children === null || children === undefined || children === "" || children === false) return null;
  return (
    <div className={c.campo}>
      <span className={c.rotulo}>{rotulo}</span>
      <span>{children}</span>
    </div>
  );
};

const Bloco = ({ titulo, children, destaque, acao }) => {
  const c = useStyles();
  return (
    <Paper variant="outlined" className={`${c.bloco} ${destaque ? c.destaque : ""}`}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        <Typography variant="subtitle2" style={{ flex: 1 }}>{titulo}</Typography>
        {acao}
      </div>
      {children}
    </Paper>
  );
};

const Copiavel = ({ valor }) => {
  const c = useStyles();
  if (!valor) return null;
  return (
    <span className={c.mono}>
      {valor}
      <IconButton size="small" title="Copiar" onClick={() => copiar(valor)}><FileCopyOutlinedIcon style={{ fontSize: 14 }} /></IconButton>
    </span>
  );
};

const AbaResumo = ({ ficha, abrirAtendimento, irPara }) => {
  const { cliente: x, resumo, ultimo, chips } = ficha;
  const endereco = [x.endereco, x.bairro, x.cidade && `${x.cidade}${x.uf ? `/${x.uf}` : ""}`, x.cep].filter(Boolean).join(", ");
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Bloco titulo="Último técnico" destaque
          acao={ultimo && <Button size="small" color="primary" onClick={() => abrirAtendimento(ultimo.id)}>Abrir atendimento</Button>}>
          {ultimo ? (
            <>
              <Typography variant="h6" style={{ lineHeight: 1.2 }}>{tecnicoTexto(ultimo.qra, ultimo.tecnico) || "Técnico não informado"}</Typography>
              <Campo rotulo="Quando">{`${dataBR(ultimo.inicio)} (${haQuanto(ultimo.inicio)})`}</Campo>
              <Campo rotulo="O.S">{ultimo.os}</Campo>
              <Campo rotulo="Serviço">{[ultimo.tipo, ultimo.sistema].filter(Boolean).join(" / ")}</Campo>
              <Campo rotulo="Situação">{SITUACAO[ultimo.status] || ultimo.status}</Campo>
            </>
          ) : <Typography color="textSecondary">Nenhum atendimento registrado.</Typography>}
        </Bloco>
      </Grid>
      <Grid item xs={12} md={6}>
        <Bloco titulo="Chips" acao={<Button size="small" onClick={() => irPara("chips")}>Detalhes</Button>}>
          {chips.chip_iccid1 || chips.chip_iccid2 || chips.chip_qtd ? (
            <>
              <Campo rotulo="Instalados">{chips.chip_qtd}</Campo>
              <Campo rotulo="ICCID chip 1"><Copiavel valor={chips.chip_iccid1} /></Campo>
              <Campo rotulo="ICCID chip 2"><Copiavel valor={chips.chip_iccid2} /></Campo>
              <Campo rotulo="Informado por">{chips.fonte && tecnicoTexto(chips.fonte.qra, chips.fonte.tecnico)}</Campo>
            </>
          ) : <Typography color="textSecondary">Nenhum chip informado ainda.</Typography>}
        </Bloco>
      </Grid>
      <Grid item xs={12} md={6}>
        <Bloco titulo="Contrato">
          <Campo rotulo="Situação"><ChipSituacao status={x.status} /></Campo>
          <Campo rotulo="Grupo">{x.grupo}</Campo>
          <Campo rotulo="Habilitado">{x.data_habilitado && dataBR(x.data_habilitado, true)}</Campo>
          <Campo rotulo="Desabilitado">{x.data_desabilitado && dataBR(x.data_desabilitado, true)}</Campo>
          <Campo rotulo="Período">{periodo(x.data_habilitado, x.data_desabilitado)}</Campo>
          <Campo rotulo="Funcionamento">{x.funcionamento}</Campo>
          <Campo rotulo="SAP">{x.sap}</Campo>
          <Campo rotulo="Código / Loja">{[x.codigo, x.loja].filter(Boolean).join(" / ")}</Campo>
          <Campo rotulo="CNPJ"><Copiavel valor={x.cnpj} /></Campo>
          <Campo rotulo="Criado em">{x.data_criacao && dataBR(x.data_criacao, true)}</Campo>
        </Bloco>
      </Grid>
      <Grid item xs={12} md={6}>
        <Grid container spacing={2} direction="column">
          <Grid item>
            <Bloco titulo="Endereço" acao={endereco && (
              <Button size="small" startIcon={<RoomOutlinedIcon />} target="_blank" rel="noopener noreferrer"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(endereco)}`}>Mapa</Button>
            )}>
              <Typography variant="body2">{endereco || "—"}</Typography>
              <Campo rotulo="Região">{x.regiao}</Campo>
            </Bloco>
          </Grid>
          <Grid item>
            <Bloco titulo="Equipamento">
              <Campo rotulo="Modelo">{x.modelo}</Campo>
              <Campo rotulo="Marca">{x.marca}</Campo>
              <Campo rotulo="MAC"><Copiavel valor={x.mac} /></Campo>
              {!x.modelo && !x.marca && !x.mac && <Typography color="textSecondary" variant="body2">Não informado.</Typography>}
            </Bloco>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <Bloco titulo="Atendimentos" acao={<Button size="small" onClick={() => irPara("atendimentos")}>Ver todos</Button>}>
          <Campo rotulo="Total">{String(resumo.total)}</Campo>
          <Campo rotulo="Em andamento">{resumo.abertos ? String(resumo.abertos) : null}</Campo>
          <Campo rotulo="Pendentes">{resumo.pendentes ? String(resumo.pendentes) : null}</Campo>
          <Campo rotulo="Primeiro">{resumo.primeiro && dataBR(resumo.primeiro, true)}</Campo>
          <Campo rotulo="Último">{resumo.ultimo && dataBR(resumo.ultimo, true)}</Campo>
          <Campo rotulo="Técnicos diferentes">{String(ficha.tecnicos.length)}</Campo>
          <Campo rotulo="Formulários">{String(ficha.formularios.length)}</Campo>
        </Bloco>
      </Grid>
      <Grid item xs={12} md={6}>
        <Bloco titulo="Observação">
          <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{x.observacao || "—"}</Typography>
          <Typography variant="caption" color="textSecondary">
            {`Última alteração do cadastro: ${x.atualizado_por || "-"} em ${dataBR(x.atualizado_em)}`}
          </Typography>
        </Bloco>
      </Grid>
    </Grid>
  );
};

const AbaAtendimentos = ({ ficha, filtroInicial, abrirAtendimento }) => {
  const c = useStyles();
  const [filtro, setFiltro] = useState(filtroInicial || "");
  useEffect(() => setFiltro(filtroInicial || ""), [filtroInicial]);
  const linhas = useMemo(() => {
    const f = filtro.trim().toUpperCase();
    if (!f) return ficha.atendimentos;
    return ficha.atendimentos.filter((a) =>
      [a.os, a.tipo, a.sistema, a.tecnico, a.qra, a.protocolo, a.protocolo_legado, a.status_os, a.analista, a.observacao]
        .some((v) => v !== null && v !== undefined && String(v).toUpperCase().includes(f)));
  }, [filtro, ficha.atendimentos]);
  return (
    <>
      <TextField size="small" variant="outlined" fullWidth placeholder="Filtrar por O.S, técnico, QRA, tipo, sistema, protocolo…"
        value={filtro} onChange={(e) => setFiltro(e.target.value)} style={{ marginBottom: 10 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      <Typography variant="caption" color="textSecondary">
        {`${linhas.length} de ${ficha.resumo.total} atendimento(s)${ficha.atendimentos.length < ficha.resumo.total ? " (mostrando os 500 mais recentes)" : ""} · clique para abrir`}
      </Typography>
      <Table size="small" className={c.tabela}>
        <TableHead>
          <TableRow>
            <TableCell>Início</TableCell><TableCell>O.S</TableCell><TableCell>Serviço</TableCell>
            <TableCell>Técnico</TableCell><TableCell>Status O.S</TableCell><TableCell>Situação</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {linhas.map((a) => (
            <TableRow key={a.id} hover onClick={() => abrirAtendimento(a.id)}>
              <TableCell style={{ whiteSpace: "nowrap" }}>{dataBR(a.inicio)}</TableCell>
              <TableCell className={c.mono}>{a.os || "—"}</TableCell>
              <TableCell>{[a.tipo, a.sistema].filter(Boolean).join(" / ")}</TableCell>
              <TableCell>{tecnicoTexto(a.qra, a.tecnico)}</TableCell>
              <TableCell>{a.status_os}</TableCell>
              <TableCell>{SITUACAO[a.status] || a.status}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

const AbaTecnicos = ({ ficha, verAtendimentos }) => {
  const c = useStyles();
  if (!ficha.tecnicos.length) return <Typography color="textSecondary">Nenhum técnico registrado nos atendimentos.</Typography>;
  return (
    <>
      <Typography variant="caption" color="textSecondary">Do mais recente para o mais antigo · clique para ver os atendimentos do técnico</Typography>
      <Table size="small" className={c.tabela}>
        <TableHead>
          <TableRow>
            <TableCell>QRA</TableCell><TableCell>Técnico</TableCell><TableCell>Situação</TableCell>
            <TableCell align="right">Atendimentos</TableCell><TableCell>Primeiro</TableCell><TableCell>Último</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ficha.tecnicos.map((t, i) => (
            <TableRow key={t.qra} hover onClick={() => verAtendimentos(String(t.qra))}>
              <TableCell><b>{t.qra}</b></TableCell>
              <TableCell>{t.tecnico}{i === 0 && <Chip size="small" color="primary" label="último" style={{ marginLeft: 6, height: 18 }} />}</TableCell>
              <TableCell>{t.status || "—"}</TableCell>
              <TableCell align="right">{t.atendimentos}</TableCell>
              <TableCell>{t.primeiro && dataBR(t.primeiro, true)}</TableCell>
              <TableCell>{t.ultimo && `${dataBR(t.ultimo, true)} (${haQuanto(t.ultimo)})`}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

const ItemFormulario = ({ resumo, abertoInicial, onMudou }) => {
  const [aberto, setAberto] = useState(!!abertoInicial);
  const [envio, setEnvio] = useState(null);
  useEffect(() => {
    if (!aberto || envio) return;
    suporteApi.get(`/api/formularios/envios/${resumo.id}`).then((r) => setEnvio(r.data)).catch((err) => toast.error(mensagemErro(err)));
  }, [aberto, envio, resumo.id]);
  const [rot, cor] = STATUS_FORM[resumo.status] || [resumo.status, "default"];
  return (
    <Accordion expanded={aberto} onChange={() => setAberto(!aberto)} TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", width: "100%" }}>
          <Chip size="small" color={cor} label={rot} />
          <b>{resumo.nome_formulario}</b>
          <span style={{ opacity: 0.75 }}>{tecnicoTexto(resumo.qra, resumo.tecnico)}</span>
          <span style={{ marginLeft: "auto", opacity: 0.75, fontSize: 12.5 }}>
            {resumo.os ? `O.S ${resumo.os} · ` : ""}
            {resumo.respondido_em ? `respondido ${dataBR(resumo.respondido_em)}` : `enviado ${dataBR(resumo.enviado_em)}`}
          </span>
        </div>
      </AccordionSummary>
      <AccordionDetails style={{ display: "block" }}>
        {envio ? (
          <RespostasEnvio envio={envio} onMudou={(e) => { setEnvio(null); onMudou && onMudou(e); }} />
        ) : <CircularProgress size={22} />}
      </AccordionDetails>
    </Accordion>
  );
};

const AbaFormularios = ({ ficha, envioAberto, onMudou }) => {
  if (!ficha.formularios.length) {
    return <Typography color="textSecondary">Nenhum formulário enviado nos atendimentos deste cliente.</Typography>;
  }
  return ficha.formularios.map((e) => (
    <ItemFormulario key={e.id} resumo={e} abertoInicial={envioAberto === e.id} onMudou={onMudou} />
  ));
};

const AbaChips = ({ ficha, verFormulario }) => {
  const { chips } = ficha;
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Bloco titulo="Chips no cadastro" destaque>
          <Campo rotulo="Instalados">{chips.chip_qtd || "—"}</Campo>
          <Campo rotulo="ICCID chip 1">{chips.chip_iccid1 ? <Copiavel valor={chips.chip_iccid1} /> : "—"}</Campo>
          <Campo rotulo="ICCID chip 2">{chips.chip_iccid2 ? <Copiavel valor={chips.chip_iccid2} /> : "—"}</Campo>
          <Campo rotulo="Atualizado">{chips.atualizado_em && `${dataBR(chips.atualizado_em)} · validado por ${chips.atualizado_por || "-"}`}</Campo>
        </Bloco>
      </Grid>
      <Grid item xs={12} md={6}>
        <Bloco titulo="Técnico que informou"
          acao={chips.fonte && <Button size="small" color="primary" onClick={() => verFormulario(chips.fonte.envio_id)}>Ver formulário</Button>}>
          {chips.fonte ? (
            <>
              <Typography variant="h6" style={{ lineHeight: 1.2 }}>{tecnicoTexto(chips.fonte.qra, chips.fonte.tecnico) || "—"}</Typography>
              <Campo rotulo="Formulário">{chips.fonte.formulario}</Campo>
              <Campo rotulo="O.S">{chips.fonte.os}</Campo>
              <Campo rotulo="Respondido">{chips.fonte.respondido_em && dataBR(chips.fonte.respondido_em)}</Campo>
              <Campo rotulo="Validado">{chips.fonte.validado_em && `${dataBR(chips.fonte.validado_em)} por ${chips.fonte.validado_por}`}</Campo>
            </>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Os chips entram no cadastro quando um formulário com perguntas de chip (quantidade e ICCID) é respondido
              pelo técnico e validado no card. Também dá para preencher à mão em “Editar”.
            </Typography>
          )}
        </Bloco>
      </Grid>
    </Grid>
  );
};

const AbaZoneamento = ({ cliente }) => {
  const z = cliente.zoneamento;
  if (!z) {
    return (
      <Typography color="textSecondary">
        Sem zoneamento. Ele chega pelo formulário do técnico (pergunta do tipo tabela marcada como “Cliente: zoneamento”).
      </Typography>
    );
  }
  return (
    <>
      <Typography variant="subtitle2" gutterBottom>
        {`${(z.linhas || []).length} zona(s) · ${cliente.zoneamento_atualizado_por || ""} em ${dataBR(cliente.zoneamento_atualizado_em)}`}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>{z.colunas.map((col) => <TableCell key={col.id}>{col.titulo}</TableCell>)}</TableRow>
        </TableHead>
        <TableBody>
          {(z.linhas || []).map((l, i) => (
            <TableRow key={i}>
              {z.colunas.map((col) => (
                <TableCell key={col.id}>
                  {col.tipo === "foto"
                    ? (l[col.id] || []).map((n, k) => (
                        <a key={n} style={{ marginRight: 6 }} target="_blank" rel="noopener noreferrer"
                          href={`${suporteApi.defaults.baseURL}/f/${z.token}/arquivos/${n}`}>{`foto ${k + 1}`}</a>
                      ))
                    : l[col.id] || "—"}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

// ------------------------------------------------------------------ ficha (gaveta com abas)
const ABAS = [
  ["resumo", "Resumo"], ["atendimentos", "Atendimentos"], ["tecnicos", "Técnicos"],
  ["formularios", "Formulários"], ["chips", "Chips"], ["zoneamento", "Zoneamento"],
];

const FichaCliente = ({ id, posicao, total, onAnterior, onProximo, onClose, onEditar, versao }) => {
  const c = useStyles();
  const [ficha, setFicha] = useState(null);
  const [aba, setAba] = useState("resumo");
  const [filtroAtend, setFiltroAtend] = useState("");
  const [envioAberto, setEnvioAberto] = useState(null);
  const [atendimento, setAtendimento] = useState(null);

  const carregar = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await suporteApi.get(`/api/clientes/${id}/ficha`);
      setFicha(data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  }, [id]);

  useEffect(() => {
    setFicha(null);
    setFiltroAtend("");
    setEnvioAberto(null);
    carregar();
  }, [carregar, versao]);

  // setas do teclado navegam entre os clientes da lista
  useEffect(() => {
    if (!id) return undefined;
    const tecla = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName) || atendimento) return;
      if (e.key === "ArrowLeft" && onAnterior) onAnterior();
      if (e.key === "ArrowRight" && onProximo) onProximo();
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [id, onAnterior, onProximo, atendimento]);

  const x = ficha?.cliente;
  const contagem = {
    atendimentos: ficha?.resumo.total, tecnicos: ficha?.tecnicos.length, formularios: ficha?.formularios.length,
    zoneamento: x?.zoneamento ? (x.zoneamento.linhas || []).length : null,
  };

  return (
    <Drawer anchor="right" open={!!id} onClose={onClose}>
      <div className={c.ficha}>
        <div className={c.fichaTopo}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Tooltip title="Anterior (←)"><span><IconButton size="small" disabled={!onAnterior} onClick={onAnterior}><ChevronLeftIcon /></IconButton></span></Tooltip>
            <Typography variant="caption" color="textSecondary">{total ? `${posicao + 1} de ${total.toLocaleString("pt-BR")}` : ""}</Typography>
            <Tooltip title="Próximo (→)"><span><IconButton size="small" disabled={!onProximo} onClick={onProximo}><ChevronRightIcon /></IconButton></span></Tooltip>
            <div style={{ flex: 1 }} />
            {x && <Button size="small" startIcon={<EditIcon />} onClick={() => onEditar(x)}>Editar</Button>}
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </div>
          {x ? (
            <div style={{ padding: "4px 4px 0" }}>
              <div className={c.linha1}>
                <Typography variant="h5" className={c.conta} style={{ fontSize: 22 }}>{x.conta}</Typography>
                <ChipSituacao status={x.status} />
                {x.grupo && <Chip size="small" variant="outlined" label={x.grupo} />}
              </div>
              <Typography variant="subtitle1">{x.nome}</Typography>
              <Typography className={c.fraco}>{[x.cidade && `${x.cidade}${x.uf ? `/${x.uf}` : ""}`, x.mac && `MAC ${x.mac}`, x.sap && `SAP ${x.sap}`].filter(Boolean).join(" · ")}</Typography>
            </div>
          ) : <div style={{ padding: 16 }}><CircularProgress size={24} /></div>}
          <Tabs value={aba} onChange={(e, v) => setAba(v)} variant="scrollable" scrollButtons="auto" indicatorColor="primary" textColor="primary">
            {ABAS.map(([v, rot]) => (
              <Tab key={v} value={v} label={contagem[v] ? `${rot} (${contagem[v]})` : rot} style={{ minWidth: 90 }} />
            ))}
          </Tabs>
        </div>
        <div className={c.fichaCorpo}>
          {ficha && aba === "resumo" && (
            <AbaResumo ficha={ficha} abrirAtendimento={setAtendimento} irPara={setAba} />
          )}
          {ficha && aba === "atendimentos" && (
            <AbaAtendimentos ficha={ficha} filtroInicial={filtroAtend} abrirAtendimento={setAtendimento} />
          )}
          {ficha && aba === "tecnicos" && (
            <AbaTecnicos ficha={ficha} verAtendimentos={(qra) => { setFiltroAtend(qra); setAba("atendimentos"); }} />
          )}
          {ficha && aba === "formularios" && (
            <AbaFormularios ficha={ficha} envioAberto={envioAberto} onMudou={carregar} />
          )}
          {ficha && aba === "chips" && (
            <AbaChips ficha={ficha} verFormulario={(eid) => { setEnvioAberto(eid); setAba("formularios"); }} />
          )}
          {ficha && aba === "zoneamento" && <AbaZoneamento cliente={x} />}
        </div>
      </div>
      <DialogCard id={atendimento} open={!!atendimento} onClose={() => { setAtendimento(null); carregar(); }} />
    </Drawer>
  );
};

// ------------------------------------------------------------------ lista
const ItemCliente = ({ x, onClick }) => {
  const c = useStyles();
  const temChip = x.chip_iccid1 || x.chip_iccid2;
  return (
    <ListItem button onClick={onClick} className={c.item} alignItems="flex-start">
      <ListItemAvatar>
        <Avatar style={{ background: COR_SITUACAO[x.status] || "#ef6c00" }}><StoreOutlinedIcon /></Avatar>
      </ListItemAvatar>
      <ListItemText
        disableTypography
        primary={
          <div className={c.linha1}>
            <span className={c.conta}>{x.conta}</span>
            <Typography component="span">{x.nome}</Typography>
            {x.status !== "ATIVO" && <ChipSituacao status={x.status} />}
            {temChip && <Tooltip title={`Chip: ${[x.chip_iccid1, x.chip_iccid2].filter(Boolean).join(" / ")}`}><SimCardOutlinedIcon fontSize="small" color="primary" /></Tooltip>}
          </div>
        }
        secondary={
          <div className={c.fraco}>
            {[x.grupo, x.cidade && `${x.cidade}${x.uf ? `/${x.uf}` : ""}`, x.bairro, x.mac && `MAC ${x.mac}`].filter(Boolean).join(" · ")}
          </div>
        }
      />
      <div className={c.lado}>
        {x.ultimo_atendimento ? (
          <>
            <div style={{ fontSize: 13 }}>{tecnicoTexto(x.ultimo_qra, x.ultimo_tecnico) || "Técnico não informado"}</div>
            <div className={c.fraco}>{`último atendimento ${haQuanto(x.ultimo_atendimento)}`}</div>
          </>
        ) : <div className={c.fraco}>sem atendimentos</div>}
      </div>
    </ListItem>
  );
};

export const BaseClientes = () => {
  const c = useStyles();
  const [filtros, setFiltros] = useState({ busca: "", status: "", grupo: "", chip: "", ordem: "conta" });
  const [busca, setBusca] = useState("");
  const [dados, setDados] = useState({ itens: [], total: 0 });
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [aberto, setAberto] = useState(null); // índice na lista carregada
  const [editando, setEditando] = useState(null);
  const [versao, setVersao] = useState(0);
  const fim = useRef(null);

  // busca enquanto digita (espera uma pausa curta)
  useEffect(() => {
    const t = setTimeout(() => setFiltros((f) => (f.busca === busca ? f : { ...f, busca })), 350);
    return () => clearTimeout(t);
  }, [busca]);

  const params = useMemo(
    () => Object.fromEntries(Object.entries(filtros).filter(([, v]) => v !== "")),
    [filtros]
  );

  const carregar = useCallback(async (pag) => {
    setCarregando(true);
    try {
      const { data } = await suporteApi.get("/api/clientes", { params: { ...params, pagina: pag, por_pagina: POR_PAGINA } });
      setDados((d) => ({ total: data.total, itens: pag === 1 ? data.itens : [...d.itens, ...data.itens] }));
      setPagina(pag);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setCarregando(false);
  }, [params]);

  useEffect(() => {
    carregar(1);
  }, [carregar]);
  useEffect(() => {
    suporteApi.get("/api/clientes/grupos").then((r) => setGrupos(r.data)).catch(() => {});
  }, []);

  const temMais = dados.itens.length < dados.total;
  // rolagem infinita: carrega a próxima página ao chegar no fim da lista
  useEffect(() => {
    const alvo = fim.current;
    if (!alvo || !temMais) return undefined;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !carregando) carregar(pagina + 1);
    }, { rootMargin: "300px" });
    obs.observe(alvo);
    return () => obs.disconnect();
  }, [temMais, carregando, carregar, pagina]);

  const filtro = (nome, valor) => setFiltros({ ...filtros, [nome]: valor });
  const limpar = () => { setBusca(""); setFiltros({ busca: "", status: "", grupo: "", chip: "", ordem: filtros.ordem }); };
  const algumFiltro = busca || filtros.status || filtros.grupo || filtros.chip;

  const irPara = (i) => {
    if (i >= dados.itens.length - 3 && temMais && !carregando) carregar(pagina + 1);
    setAberto(i);
  };
  const atual = aberto !== null ? dados.itens[aberto] : null;

  const salvo = (registro) => {
    setVersao((v) => v + 1);
    setDados((d) => {
      const i = d.itens.findIndex((x) => x.id === registro.id);
      if (i < 0) return d;
      const itens = [...d.itens];
      itens[i] = { ...itens[i], ...registro };
      return { ...d, itens };
    });
    if (!dados.itens.some((x) => x.id === registro.id)) carregar(1);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{`Base clientes (${dados.total.toLocaleString("pt-BR")})`}</Title>
        <MainHeaderButtonsWrapper>
          <Button startIcon={<AddIcon />} variant="contained" color="primary" onClick={() => setEditando({ status: "ATIVO" })}>Novo</Button>
          <Button startIcon={<GetAppIcon />} onClick={() => baixar("/api/exportar/clientes.xlsx", params).catch((err) => toast.error(mensagemErro(err)))}>
            Excel
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper variant="outlined" className={c.filtros}>
        <TextField
          className={c.busca} size="small" variant="outlined" autoFocus value={busca}
          placeholder="Buscar por conta, filial, cidade, bairro, CNPJ, MAC, SAP, ICCID ou técnico (nome/QRA)"
          onChange={(e) => setBusca(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            endAdornment: carregando && <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>,
          }}
        />
        <TextField select size="small" variant="outlined" label="Grupo" style={{ minWidth: 160 }}
          value={filtros.grupo} onChange={(e) => filtro("grupo", e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {grupos.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
        </TextField>
        <TextField select size="small" variant="outlined" label="Ordenar" style={{ minWidth: 170 }}
          value={filtros.ordem} onChange={(e) => filtro("ordem", e.target.value)}>
          <MenuItem value="conta">Grupo e conta</MenuItem>
          <MenuItem value="recentes">Atendidos recentemente</MenuItem>
          <MenuItem value="nome">Nome da filial</MenuItem>
        </TextField>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", width: "100%" }}>
          {[["", "Todas"], ...SITUACOES.map((s) => [s, s])].map(([v, rot]) => (
            <Chip key={rot} size="small" label={rot} clickable
              color={filtros.status === v ? "primary" : "default"} onClick={() => filtro("status", v)} />
          ))}
          <span style={{ width: 12 }} />
          {[["com", "Com chip"], ["sem", "Sem chip"]].map(([v, rot]) => (
            <Chip key={v} size="small" label={rot} clickable icon={<SimCardOutlinedIcon />}
              color={filtros.chip === v ? "primary" : "default"} onClick={() => filtro("chip", filtros.chip === v ? "" : v)} />
          ))}
          {algumFiltro && <Button size="small" onClick={limpar}>Limpar filtros</Button>}
        </div>
      </Paper>

      <Paper variant="outlined" className={c.lista}>
        <List disablePadding>
          {dados.itens.map((x, i) => <ItemCliente key={x.id} x={x} onClick={() => setAberto(i)} />)}
        </List>
        {!carregando && !dados.itens.length && (
          <Typography color="textSecondary" style={{ padding: 24, textAlign: "center" }}>Nenhum cliente encontrado.</Typography>
        )}
        <div ref={fim} style={{ padding: 12, textAlign: "center" }}>
          {temMais && (carregando ? <CircularProgress size={22} /> : <Button onClick={() => carregar(pagina + 1)}>Carregar mais</Button>)}
        </div>
      </Paper>

      <FichaCliente
        id={atual?.id}
        versao={versao}
        posicao={aberto ?? 0}
        total={dados.total}
        onAnterior={aberto > 0 ? () => irPara(aberto - 1) : null}
        onProximo={aberto !== null && aberto < dados.total - 1 && aberto < dados.itens.length - 1 ? () => irPara(aberto + 1) : null}
        onClose={() => setAberto(null)}
        onEditar={(x) => setEditando(x)}
      />
      <FormCadastro registro={editando} onClose={() => setEditando(null)} onSalvo={salvo} />
    </MainContainer>
  );
};
